import React, {useState} from 'react';
import {useFormik} from 'formik';
import * as Yup from 'yup';
import {ToastContainer, toast} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './SupplierInformation.module.css';
import globalStyles from '../../styles/globalStyles.module.css';
import supplier from '../../assets/images/supplier.svg';
import {IoIosArrowDown} from 'react-icons/io';
import {useLocation, useNavigate} from 'react-router-dom';
import {postFetcher} from '../../../api/fetchers';
import {endpoints} from '../../../api/endpoints';
import { useLanguage } from '../../context/LanguageContext';

const SupplierApplicationForm: React.FC = () => {
    const {t} = useLanguage();  
    const location = useLocation();
    const navigate = useNavigate();
    const {formData} = location.state || {};
    const [isInvoiceTypeDropdownOpen, setIsInvoiceTypeDropdownOpen] = useState<boolean>(false);
    const invoiceTypes = ["E-Arşiv Fatura", "Kağıt Fatura"];
    const formik = useFormik({
        initialValues: {
            fullName: formData?.fullName || '',
            email: formData?.email || '',
            phoneNumber: formData?.phoneNumber || '',
            city: formData?.city || '',
            district: formData?.district || '',
            companyTypeId: formData?.companyTypeId || '',
            supplierProductTypeId: formData?.supplierProductTypeId || '',
            identityNumber: formData?.identityNumber || '',
            companyName: formData?.companyName || '',
            marketPlaceName: '',
            taxNumber: '',
            taxOffice: '',
            kepAddress: '',
            openAddress: '',
            mersisNumber: '',
            invoiceType: '',
        },
        validationSchema: Yup.object({
            marketPlaceName: Yup.string().required('Depo / Pazar adı gerekli'),
            taxNumber: Yup.string().required('Vergi numarası gerekli'),
            taxOffice: Yup.string().required('Vergi dairesi gerekli'),
            kepAddress: Yup.string().required('KEP adresi gerekli'),
            openAddress: Yup.string().required('Açık adresi gerekli'),
            mersisNumber: Yup.string().required('Mersis numarası gerekli'),
            invoiceType: Yup.string().required('Fatura tipi gerekli'),
        }),
        onSubmit: async (values) => {
            const combinedValues = {
                ...formData,
                ...values
            };

            try {
                const response = await postFetcher(endpoints.auth.registerSupplier, {arg: combinedValues});
                if (response) {
                    toast.info(t('register', 'e2'));
                    setTimeout(() => {
                        navigate('/login');
                    }, 2000);
                } else {
                    throw new Error('Supplier creation failed');
                }
            } catch (error) {
                toast.error('Error');
                console.error('Error submitting form', error);
            }
        }
    });


    const toggleInvoiceTypeDropdown = () => {
        setIsInvoiceTypeDropdownOpen(!isInvoiceTypeDropdownOpen);
    };

    const handleInvoiceTypeSelect = (value: string) => {
        formik.setFieldValue('invoiceType', value);
        setIsInvoiceTypeDropdownOpen(false);
    };

    return (
        <div className="flex flex-col md:flex-row h-screen w-screen bg-primary-color">
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop={false}
                            closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover/>
            {/* LEFT SIDE */}
            <div className={`w-full md:w-1/2 flex items-center justify-center ${styles.leftSide}`}>
                <img src={supplier} alt="Supplier Illustration" className="object-cover"/>
            </div>

            {/* RIGHT SIDE */}
            <div
                className={`w-full md:w-1/2 h-full flex items-center justify-center bg-white ${globalStyles.rightSide}`}>
                <div className="w-full max-w-md p-8 space-y-8">
                    <form className="space-y-8" onSubmit={formik.handleSubmit}>
                        <div className="relative">
                            <input
                                type="text"
                                id="marketPlaceName"
                                name="marketPlaceName"
                                placeholder="Depo / Pazar Adı"
                                value={formik.values.marketPlaceName}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={`block w-full py-2 pl-2 font-bold text-primary-color mt-1 bg-transparent border-b-2 border-color-pink placeholder-color-bold-gray placeholder:font-bold focus:outline-none shadow-sm sm:text-sm  ${formik.errors.marketPlaceName && formik.touched.marketPlaceName ? 'border-red-500' : ''}`}
                            />
                            {formik.errors.marketPlaceName && formik.touched.marketPlaceName && (
                                <div className="text-red-500 text-sm">{formik.errors.marketPlaceName}</div>
                            )}
                        </div>

                        <div className="relative">
                            <input
                                type="text"
                                id="taxNumber"
                                name="taxNumber"
                                placeholder="Vergi Numarası"
                                value={formik.values.taxNumber}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={`block w-full py-2 pl-2 font-bold text-primary-color mt-1 bg-transparent border-b-2 border-color-pink placeholder-color-bold-gray placeholder:font-bold focus:outline-none shadow-sm sm:text-sm  ${formik.errors.taxNumber && formik.touched.taxNumber ? 'border-red-500' : ''}`}
                            />
                            {formik.errors.taxNumber && formik.touched.taxNumber && (
                                <div className="text-red-500 text-sm">{formik.errors.taxNumber}</div>
                            )}
                        </div>

                        <div className="relative">
                            <input
                                type="text"
                                id="taxOffice"
                                name="taxOffice"
                                placeholder="Vergi Yeri"
                                value={formik.values.taxOffice}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={`block w-full py-2 pl-2 font-bold text-primary-color mt-1 bg-transparent border-b-2 border-color-pink placeholder-color-bold-gray placeholder:font-bold focus:outline-none shadow-sm sm:text-sm  ${formik.errors.taxOffice && formik.touched.taxOffice ? 'border-red-500' : ''}`}
                            />
                            {formik.errors.taxOffice && formik.touched.taxOffice && (
                                <div className="text-red-500 text-sm">{formik.errors.taxOffice}</div>
                            )}
                        </div>

                        <div className="relative">
                            <input
                                type="text"
                                id="kepAddress"
                                name="kepAddress"
                                placeholder="Kep Adresi"
                                value={formik.values.kepAddress}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={`block w-full py-2 pl-2 font-bold text-primary-color mt-1 bg-transparent border-b-2 border-color-pink placeholder-color-bold-gray placeholder:font-bold focus:outline-none shadow-sm sm:text-sm ${formik.errors.kepAddress && formik.touched.kepAddress ? 'border-red-500' : ''}`}
                            />
                            {formik.errors.kepAddress && formik.touched.kepAddress && (
                                <div className="text-red-500 text-sm">{formik.errors.kepAddress}</div>
                            )}
                        </div>

                        <div className="relative">
                            <input
                                type="text"
                                id="openAddress"
                                name="openAddress"
                                placeholder="Açık Adres"
                                value={formik.values.openAddress}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={`block w-full py-2 pl-2 font-bold text-primary-color mt-1 bg-transparent border-b-2 border-color-pink placeholder-color-bold-gray placeholder:font-bold focus:outline-none shadow-sm sm:text-sm ${formik.errors.openAddress && formik.touched.openAddress ? 'border-red-500' : ''}`}
                            />
                            {formik.errors.openAddress && formik.touched.openAddress && (
                                <div className="text-red-500 text-sm">{formik.errors.openAddress}</div>
                            )}
                        </div>

                        <div className="relative">
                            <input
                                type="text"
                                id="mersisNumber"
                                name="mersisNumber"
                                placeholder="Mersis No"
                                value={formik.values.mersisNumber}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={`block w-full py-2 pl-2 font-bold text-primary-color mt-1 bg-transparent border-b-2 border-color-pink placeholder-color-bold-gray placeholder:font-bold focus:outline-none shadow-sm sm:text-sm ${formik.errors.mersisNumber && formik.touched.mersisNumber ? 'border-red-500' : ''}`}
                            />
                            {formik.errors.mersisNumber && formik.touched.mersisNumber && (
                                <div className="text-red-500 text-sm">{formik.errors.mersisNumber}</div>
                            )}
                        </div>

                        <div className="relative inline-block text-left w-full">
                            <button
                                type="button"
                                className={`inline-flex justify-between w-full py-2 pl-2 font-bold mt-1 bg-transparent border-b-2 border-color-pink focus:outline-none shadow-sm sm:text-sm ${formik.values.invoiceType ? 'text-primary-color' : 'text-color-bold-gray'}`}
                                onClick={toggleInvoiceTypeDropdown}
                            >
                                {formik.values.invoiceType || 'Fatura Tipi'}
                                <IoIosArrowDown className="ml-2 h-5 w-5 text-gray-400"/>
                            </button>
                            {isInvoiceTypeDropdownOpen && (
                                <div
                                    className="absolute right-0 z-10 mt-2 w-full max-h-32 overflow-y-auto origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                                    role="menu" aria-orientation="vertical">
                                    <div className="py-1" role="none">
                                        {invoiceTypes.map(type => (
                                            <a href="#" key={type}
                                               className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                               role="menuitem"
                                               onClick={() => handleInvoiceTypeSelect(type)}>{type}</a>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {formik.errors.invoiceType && formik.touched.invoiceType && (
                                <div className="text-red-500 text-sm">{formik.errors.invoiceType}</div>
                            )}
                        </div>

                        <button
                            type="submit"
                            className="w-full px-4 py-2 pl-2 font-bold text-white bg-color-pink rounded-md hover:bg-hover-color hover:text-color-pink focus:ring-2 focus:ring-pink-500"
                        >
                            Gönder
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default SupplierApplicationForm;
